/**
 * Copyright (c) 2026 Lauft App Inc. All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 */

import React, { useState, useEffect, useCallback } from 'react';
import AuthService from '../../services/authService';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

// Active-users signal toggles. Acquisition / engagement / messaging metrics
// from /engagement are always returned in full — only the active-users union
// is reshaped client-side via these toggles.
const SIGNAL_OPTIONS = [
  { key: 'swipe',            label: 'Swipes',            default: true },
  { key: 'login',            label: 'Logins',            default: true },
  { key: 'message_sent',     label: 'Messages sent',     default: true },
  { key: 'message_received', label: 'Messages received', default: true },
];

const toISODate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

const PRESETS = [
  { label: '7d',  days: 7  },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
];

// Tile builders for grouped engagement cards. Each tile has a label, a value
// getter, and an optional `lifetime: true` flag for metrics that aren't bound
// to the date window (swipes/likes/dislikes — no per-swipe timestamps exist).
const ENGAGEMENT_GROUPS = [
  {
    title: 'Acquisition',
    tiles: [
      { key: 'newSignups',   label: 'New Signups',     get: e => e?.acquisition?.newSignups },
      { key: 'quizzesTaken', label: 'LauftQ Quizzes',  get: e => e?.acquisition?.quizzesTaken },
    ]
  },
  {
    title: 'Engagement',
    tiles: [
      { key: 'totalSwipes',         label: 'Total Swipes',         get: e => e?.engagement?.totalSwipes,         lifetime: true },
      { key: 'totalLikes',          label: 'Likes',                get: e => e?.engagement?.totalLikes,          lifetime: true },
      { key: 'totalDislikes',       label: 'Dislikes',             get: e => e?.engagement?.totalDislikes,       lifetime: true },
      { key: 'propertiesFavorited', label: 'Properties Favorited', get: e => e?.engagement?.propertiesFavorited, lifetime: true },
      { key: 'savedAreasCreated',   label: 'Saved Areas',          get: e => e?.engagement?.savedAreasCreated   },
      { key: 'dreamBoardsCreated',  label: 'Dream Boards',         get: e => e?.engagement?.dreamBoardsCreated  },
      { key: 'questionsAsked',      label: 'Questions Asked',      get: e => e?.engagement?.questionsAsked      },
    ]
  },
  {
    title: 'Messaging & Matching',
    tiles: [
      { key: 'userMessagesSent',   label: 'User Messages',      get: e => e?.messagingMatching?.userMessagesSent   },
      { key: 'agentMessagesSent',  label: 'Agent Messages',     get: e => e?.messagingMatching?.agentMessagesSent  },
      { key: 'curatedMatchesSent', label: 'Curated Matches',    get: e => e?.messagingMatching?.curatedMatchesSent },
      { key: 'curatedMatchViews',  label: 'Match Views',        get: e => e?.messagingMatching?.curatedMatchViews  },
      { key: 'showingsRequested',  label: 'Showings Requested', get: e => e?.messagingMatching?.showingsRequested  },
    ]
  },
  {
    title: 'Social',
    tiles: [
      { key: 'agentsInvited',         label: 'Users Inviting Agents', get: e => e?.social?.agentsInvited,         lifetime: true },
      { key: 'agentInvitesToUsers',   label: 'Agent Invites to Users',get: e => e?.social?.agentInvitesToUsers    },
      { key: 'connectionInvitesSent', label: 'Connection Invites',    get: e => e?.social?.connectionInvitesSent },
    ]
  },
];

const UserMetricsPanel = () => {
  const [startDate, setStartDate] = useState(toISODate(daysAgo(30)));
  const [endDate, setEndDate]     = useState(toISODate(new Date()));

  const [signals, setSignals] = useState(() => {
    const init = {};
    SIGNAL_OPTIONS.forEach(s => { init[s.key] = s.default; });
    return init;
  });

  const [activeData, setActiveData]         = useState(null);
  const [engagementData, setEngagementData] = useState(null);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState('');

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const selectedSignals = Object.entries(signals)
        .filter(([, on]) => on)
        .map(([k]) => k);

      if (selectedSignals.length === 0) {
        setError('Select at least one signal to compute active users.');
        setLoading(false);
        return;
      }

      const token = AuthService.getToken();
      const headers = { Authorization: `Bearer ${token}` };

      const activeParams = new URLSearchParams({
        start: startDate,
        end: endDate,
        signals: selectedSignals.join(','),
      });
      const engagementParams = new URLSearchParams({
        start: startDate,
        end: endDate,
      });

      // Fire both endpoints in parallel; engagement is the wider blast radius
      // so its error message takes priority if both fail.
      const [activeRes, engagementRes] = await Promise.all([
        fetch(`${API_URL}/admin/metrics/active-users?${activeParams}`, { headers }),
        fetch(`${API_URL}/admin/metrics/engagement?${engagementParams}`,  { headers }),
      ]);

      const [activeJson, engagementJson] = await Promise.all([
        activeRes.json(),
        engagementRes.json(),
      ]);

      if (!engagementRes.ok || !engagementJson.success) {
        throw new Error(engagementJson.error || `Engagement request failed (${engagementRes.status})`);
      }
      if (!activeRes.ok || !activeJson.success) {
        throw new Error(activeJson.error || `Active-users request failed (${activeRes.status})`);
      }

      setActiveData(activeJson);
      setEngagementData(engagementJson);
    } catch (err) {
      setError(err.message || 'Failed to load metrics.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, signals]);

  useEffect(() => {
    fetchMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSignal = (key) => {
    setSignals(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const applyPreset = (days) => {
    setStartDate(toISODate(daysAgo(days)));
    setEndDate(toISODate(new Date()));
  };

  const handleRefresh = () => fetchMetrics();

  const breakdown = activeData?.breakdown || {};
  const maxBreakdown = Math.max(1, ...Object.values(breakdown));

  // Format helper: number with commas, skeleton while loading, em-dash if null.
  const fmt = (v) => {
    if (loading) return <span className="skeleton">000</span>;
    if (v == null || Number.isNaN(v)) return '—';
    return Number(v).toLocaleString();
  };

  return (
    <div className="admin-panel" style={{ color: '#111', padding: '32px 0 0' }}>
      <style>{`
        /* Metrics — matches Control Center design system from UserAssignmentManager */

        .admin-panel .dashboard-card {
          background: #fff;
          border-radius: 14px;
          padding: 20px;
          border: 1px solid #f0f0f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          margin-bottom: 16px;
        }

        .admin-panel .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .admin-panel .card-header h3 {
          margin: 0;
          font-size: 11px;
          font-weight: 600;
          color: #9ca3af;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        .admin-panel .alert-danger {
          background-color: rgba(220, 38, 38, 0.08);
          color: #991b1b;
          border: 1px solid rgba(220, 38, 38, 0.25);
          padding: 11px 14px;
          border-radius: 10px;
          margin-bottom: 16px;
          font-size: 13px;
          font-weight: 500;
        }

        .admin-panel .field-label {
          display: block;
          font-size: 10px;
          font-weight: 700;
          color: #9ca3af;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: 6px;
        }
        .admin-panel .date-input {
          padding: 8px 10px;
          border: 1px solid #e5e7eb;
          border-radius: 9px;
          font-size: 13px;
          color: #111;
          background: #fff;
          outline: none;
          font-family: inherit;
          min-width: 150px;
        }
        .admin-panel .date-input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }

        .admin-panel .btn {
          padding: 8px 16px;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
          font-size: 13px;
          letter-spacing: 0.01em;
          border: 1px solid #e5e7eb;
          background: #fff;
          color: #6b7280;
          font-family: inherit;
        }
        .admin-panel .btn:hover:not(:disabled) { background: #f9fafb; color: #111; }
        .admin-panel .btn:disabled { opacity: 0.6; cursor: default; }
        .admin-panel .btn-primary {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          color: #fff;
          border: none;
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.35);
        }
        .admin-panel .btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #1d4ed8, #1e40af);
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.45);
          color: #fff;
        }

        .admin-panel .preset-chip {
          padding: 6px 12px;
          border-radius: 999px;
          border: 1px solid #e5e7eb;
          background: #fff;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.04em;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.15s;
        }
        .admin-panel .preset-chip:hover { background: #f9fafb; color: #111; }

        .admin-panel .signal-chip {
          padding: 7px 14px;
          border-radius: 999px;
          border: 1px solid #e5e7eb;
          background: #fff;
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.15s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .admin-panel .signal-chip:hover { background: #f9fafb; color: #111; }
        .admin-panel .signal-chip.on {
          background: rgba(37, 99, 235, 0.08);
          border-color: rgba(37, 99, 235, 0.35);
          color: #1d4ed8;
        }
        .admin-panel .signal-chip .dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: #e5e7eb;
        }
        .admin-panel .signal-chip.on .dot { background: #2563eb; }

        /* Top summary stat tiles */
        .admin-panel .stat-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
        }
        @media (max-width: 720px) {
          .admin-panel .stat-grid { grid-template-columns: 1fr; }
        }
        .admin-panel .stat-tile {
          background: #fafafa;
          border: 1px solid #f0f0f0;
          border-radius: 12px;
          padding: 18px;
        }
        .admin-panel .stat-tile.primary {
          background: linear-gradient(135deg, rgba(37, 99, 235, 0.06), rgba(29, 78, 216, 0.04));
          border-color: rgba(37, 99, 235, 0.18);
        }
        .admin-panel .stat-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #9ca3af;
          margin-bottom: 8px;
        }
        .admin-panel .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: #111;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }
        .admin-panel .stat-tile.primary .stat-value { color: #1d4ed8; }
        .admin-panel .stat-sub {
          font-size: 11px;
          color: #6b7280;
          margin-top: 6px;
        }

        /* Compact tiles for grouped engagement cards — auto-fit so each row
           fills cleanly regardless of how many tiles in the group. */
        .admin-panel .mini-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 10px;
        }
        .admin-panel .mini-tile {
          background: #fafafa;
          border: 1px solid #f0f0f0;
          border-radius: 10px;
          padding: 14px;
          position: relative;
        }
        .admin-panel .mini-tile .mini-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #9ca3af;
          margin-bottom: 6px;
        }
        .admin-panel .mini-tile .mini-value {
          font-size: 22px;
          font-weight: 700;
          color: #111;
          letter-spacing: -0.01em;
          line-height: 1.1;
        }
        .admin-panel .mini-tile .lifetime-badge {
          position: absolute;
          top: 8px; right: 8px;
          font-size: 8px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #9ca3af;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          padding: 2px 5px;
        }

        /* Breakdown bars */
        .admin-panel .bar-row {
          display: grid;
          grid-template-columns: 160px 1fr 60px;
          gap: 12px;
          align-items: center;
          padding: 9px 0;
          border-bottom: 1px solid #f5f5f5;
        }
        .admin-panel .bar-row:last-child { border-bottom: none; }
        .admin-panel .bar-row.muted .bar-label,
        .admin-panel .bar-row.muted .bar-value { color: #c7c9cd; }
        .admin-panel .bar-row.muted .bar-fill { background: #ececec; }
        .admin-panel .bar-label {
          font-size: 13px;
          font-weight: 600;
          color: #111;
        }
        .admin-panel .bar-track {
          height: 8px;
          background: #f3f4f6;
          border-radius: 999px;
          overflow: hidden;
        }
        .admin-panel .bar-fill {
          height: 100%;
          background: linear-gradient(90deg, #2563eb, #1d4ed8);
          border-radius: 999px;
          transition: width 0.4s ease;
        }
        .admin-panel .bar-value {
          font-size: 13px;
          font-weight: 700;
          color: #111;
          text-align: right;
          font-variant-numeric: tabular-nums;
        }

        .admin-panel .skeleton {
          background: linear-gradient(90deg, #f3f4f6 0%, #fafafa 50%, #f3f4f6 100%);
          background-size: 200% 100%;
          animation: skel 1.4s linear infinite;
          border-radius: 6px;
          color: transparent;
        }
        @keyframes skel { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
      `}</style>

      {/* Branding */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <img src="/lauft-logo.png" alt="Lauft" style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
        <span style={{
          fontSize: '11px', fontWeight: '600',
          letterSpacing: '0.08em', color: '#888',
          textTransform: 'uppercase'
        }}>
          User Metrics
        </span>
      </div>

      {error && <div className="alert-danger">{error}</div>}

      {/* Controls */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Filters</h3>
          <button
            className="btn btn-primary"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end', marginBottom: '16px' }}>
          <div>
            <label className="field-label">Start date</label>
            <input
              type="date"
              className="date-input"
              value={startDate}
              max={endDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="field-label">End date</label>
            <input
              type="date"
              className="date-input"
              value={endDate}
              min={startDate}
              max={toISODate(new Date())}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: '6px', paddingBottom: '1px' }}>
            {PRESETS.map(p => (
              <button
                key={p.label}
                className="preset-chip"
                onClick={() => applyPreset(p.days)}
                type="button"
              >
                Last {p.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="field-label">Activity signals (union)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {SIGNAL_OPTIONS.map(opt => (
              <button
                key={opt.key}
                type="button"
                className={`signal-chip ${signals[opt.key] ? 'on' : ''}`}
                onClick={() => toggleSignal(opt.key)}
              >
                <span className="dot" />
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>
            Toggle which signals count toward Active Users, then hit Refresh. Engagement metrics below always update with the date window.
          </div>
        </div>
      </div>

      {/* Headline Summary */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Summary</h3>
          {activeData && (
            <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600 }}>
              {activeData.windowDays} day window · {new Date(activeData.start).toLocaleDateString()} – {new Date(activeData.end).toLocaleDateString()}
            </span>
          )}
        </div>

        <div className="stat-grid">
          <div className="stat-tile primary">
            <div className="stat-label">Active Users</div>
            <div className="stat-value">{fmt(activeData?.activeUsers)}</div>
            <div className="stat-sub">
              Distinct users matching at least one selected signal
            </div>
          </div>

          <div className="stat-tile">
            <div className="stat-label">New Signups</div>
            <div className="stat-value">{fmt(activeData?.newSignups)}</div>
            <div className="stat-sub">
              Users created in this window
            </div>
          </div>

          <div className="stat-tile">
            <div className="stat-label">Signals Used</div>
            <div className="stat-value" style={{ fontSize: '20px' }}>
              {loading ? <span className="skeleton">000</span>
                       : (activeData?.signalsUsed?.length ?? 0)} / {SIGNAL_OPTIONS.length}
            </div>
            <div className="stat-sub">
              {activeData?.signalsUsed?.join(', ') || '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Active-users breakdown */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3>Active Users · Breakdown by signal</h3>
          <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600 }}>
            Distinct users per signal · grayed = not in current union
          </span>
        </div>

        {SIGNAL_OPTIONS.map(opt => {
          const isOn = !!signals[opt.key];
          const count = breakdown[opt.key] ?? 0;
          const pct = maxBreakdown > 0 ? (count / maxBreakdown) * 100 : 0;
          return (
            <div key={opt.key} className={`bar-row ${isOn ? '' : 'muted'}`}>
              <div className="bar-label">{opt.label}</div>
              <div className="bar-track">
                <div className="bar-fill" style={{ width: `${pct}%` }} />
              </div>
              <div className="bar-value">
                {loading ? <span className="skeleton">000</span> : count.toLocaleString()}
              </div>
            </div>
          );
        })}

        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '12px' }}>
          Per-signal counts overlap — most active users appear in multiple. Adding the bars will exceed the Active Users total.
        </div>
      </div>

      {/* Grouped engagement cards */}
      {ENGAGEMENT_GROUPS.map(group => (
        <div className="dashboard-card" key={group.title}>
          <div className="card-header">
            <h3>{group.title}</h3>
          </div>

          <div className="mini-grid">
            {group.tiles.map(tile => (
              <div className="mini-tile" key={tile.key}>
                {tile.lifetime && <span className="lifetime-badge">All-time</span>}
                <div className="mini-label">{tile.label}</div>
                <div className="mini-value">{fmt(tile.get(engagementData))}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default UserMetricsPanel;